import React from "react";
import A4PageFrame from "./A4PageFrame";
import { fmtNum, fmtPct } from "./formatters";

// Phase 3 (B3): Cost summary — mirrors PDF page 5 ("Résumé des coûts").
//
// Per-account row: Compte, Désignation, Clé, current HT, current TTC, prior HT,
// Diff. HT, Diff. %, Part %. Total CHF row at the bottom.
// Pass #3: the client asked to DELETE the graphs here ("too tight") — this is now
// a TABLE-ONLY page, no donut/bar chart.
//
// Étang A feedback 2026-07-17: client reported "Missing 2nd page with missing
// costs and total". Étang A has 41 cost accounts vs Étang B's 26, and 41 rows
// + header + Total CHF row do NOT fit one portrait A4 page — the trailing
// accounts and the Total CHF row were clipped by the frame's overflow:hidden.
// Fix: paginate the table (COST_SUMMARY_ROWS_PER_PAGE below), rendering the
// Total CHF row ONLY on the last page. Preview.js uses the same constant so
// page-number math stays in sync.
//
// Props
//   data             response from GET /v1/repartition-report/cost-summary
//   periodYears      { current, prior? } for chart legend
//   compareEnabled   bool — hides prior, Diff, % columns when false

// Rows-per-page — client feedback 2026-07-21: "all costs on one page".
// Étang A 2025 has 44 accounts + 1 Total CHF row = 45 rows.
//
// History:
//   1) Cap 45 with lineHeight 12/padding 2 fit 41-account Étang A 23/24 on
//      one page, but broke after we added the "Connection" column (that
//      narrowed Désignation/Clé, so ~15 rows wrap to 2 lines, and only ~34
//      visually fit before overflow: hidden clipped the tail).
//   2) Cap dropped to 30 forced a split into 30 + 14 — no rows lost, but the
//      client didn't want a second page ("try to put it in 1").
//   3) Now (2026-07-24): cap raised to 60 (any realistic building on 1 page)
//      AND row height dropped by tightening `td` below — lineHeight 12→10 +
//      padding 2→1 shaves 4 px off each wrapped row, so 45 rows with 15
//      wraps come out to ~795 px in the ~916 px body band. Font stays 9.5
//      so cell text stays readable at print size.
// Raised 60 → 64 on 2026-08-06. Client (Espace Tourbillon): "Resume couts: fullfil 4
// to 6 cost lines on page 1 to avoid empty space". Measured in Chrome at the old
// budget: a 60-row page left 73 px free = 5 rows of 14 px. 64 uses four of them and
// keeps a ~17 px cushion for sub-pixel rounding during html-to-image capture.
export const COST_SUMMARY_ROWS_PER_PAGE = 64;

const CostSummary = ({
  data,
  building,
  periodLabel,
  periodYears,
  compareEnabled,
  whiteLabelling,
  logoUrl,
  basePageNumber = 1,
  grandTotalPages = 1,
}) => {
  const costs = data?.costs || [];
  const totals = data?.totals || {};
  const currentYear = periodYears?.current ?? "Current";
  const priorYear   = periodYears?.prior   ?? "Prior";

  const pages = [];
  for (let i = 0; i < costs.length; i += COST_SUMMARY_ROWS_PER_PAGE) {
    pages.push(costs.slice(i, i + COST_SUMMARY_ROWS_PER_PAGE));
  }
  if (pages.length === 0) pages.push([]);

  return (
    <>
      {pages.map((rows, pageIdx) => {
        const isLastPage = pageIdx === pages.length - 1;
        return (
          <A4PageFrame
            key={pageIdx}
            building={building}
            periodLabel={periodLabel}
            sectionTitle={`Résumé des coûts${pages.length > 1 ? ` (${pageIdx + 1}/${pages.length})` : ""}`}
            whiteLabelling={whiteLabelling}
            logoUrl={logoUrl}
            pageNumber={basePageNumber + pageIdx}
            totalPages={grandTotalPages}
          >
            <table style={tbl}>
              <thead>
                <tr style={{ borderBottom: "1px solid #00033F" }}>
                  <th style={th}>Compte</th>
                  <th style={th}>Désignation</th>
                  <th style={th}>Clé</th>
                  {/* Client 2026-07-22: new "Connection" column — ETC / DJF / NJ.
                      Backend mirrors the Result II engine's precedence
                      (key cost_fields_data > tbl_cost.connection > 'NJ')
                      so this column reflects what actually got applied. */}
                  <th style={th}>Connection</th>
                  <th style={thNum}>{currentYear} HT</th>
                  <th style={thNum}>{currentYear} TTC</th>
                  {compareEnabled && <th style={thNum}>{priorYear} HT</th>}
                  {compareEnabled && <th style={thNum}>Diff. HT</th>}
                  {compareEnabled && <th style={thNum}>Diff. %</th>}
                  <th style={thNum}>Part %</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.cost}>
                    <td style={td}>{c.cost}</td>
                    <td style={td}>{c.name || ""}</td>
                    {/* M1.1: Clé column shows the distribution-key name(s) from
                        tbl_key.key_name (via tbl_key_cost).
                        M1.4 fix: removed the c.type fallback. Once the client
                        started populating real key links, the old fallback was
                        surfacing the currency code ("CHF") in the Clé column for
                        accounts without any key configured — the reference PDF
                        shows an empty cell in that case. */}
                    <td style={td}>{c.key_name || ""}</td>
                    <td style={td}>{c.connection || ""}</td>
                    <td style={tdNum}>{fmtNum(c.main_ht)}</td>
                    <td style={tdNum}>{fmtNum(c.main_ttc)}</td>
                    {compareEnabled && <td style={tdNum}>{fmtNum(c.compare_ht)}</td>}
                    {compareEnabled && <td style={tdNum}>{fmtNum(c.diff)}</td>}
                    {compareEnabled && <td style={tdNum}>{fmtPct(c.diff_pct)}</td>}
                    <td style={tdNum}>{fmtPct(c.part_pct)}</td>
                  </tr>
                ))}
                {isLastPage && (
                  <tr style={{ fontWeight: 700, borderTop: "1px solid #00033F" }}>
                    <td style={td} />
                    <td style={td}>Total CHF</td>
                    <td style={td} />
                    <td style={td} />
                    <td style={tdNum}>{fmtNum(totals.main_ht)}</td>
                    <td style={tdNum}>{fmtNum(totals.main_ttc)}</td>
                    {compareEnabled && <td style={tdNum}>{fmtNum(totals.compare_ht)}</td>}
                    {compareEnabled && <td style={tdNum}>{fmtNum(totals.diff)}</td>}
                    {compareEnabled && <td style={tdNum}>{fmtPct(totals.diff_pct)}</td>}
                    <td style={tdNum} />
                  </tr>
                )}
              </tbody>
            </table>
          </A4PageFrame>
        );
      })}
    </>
  );
};

const tbl = { width: "100%", borderCollapse: "collapse", marginTop: 4 };
const th = { padding: "2px 4px", fontWeight: 600, textAlign: "left", fontSize: 10 };
const thNum = { ...th, textAlign: "right" };
// Row-height math after the 2026-07-24 tightening:
//   single-line row: lineHeight 11 + padding 1×2 + border 1 = 14 px
//   two-line wrap:   lineHeight 22 + padding 1×2 + border 1 = 25 px
//   45 rows w/ ~15 wraps ⇒ 30·14 + 15·25 = 795 px, comfortably inside the
//   ~916 px A4 portrait body band. Font stays 9.5; lineHeight kept slightly
//   above the font size so ascenders/descenders don't bleed into the next row.
const td = { padding: "1px 5px", fontSize: 9.5, lineHeight: "11px", borderBottom: "1px solid rgba(0,3,63,0.05)" };
const tdNum = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };

export default CostSummary;
